import { useState } from "react";
import "./leaseDetails.css";
import { DocumentService } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { convertStreamAndDownload } from "../../../utils/helper";
import SimpleSpinner from "../../../components/loader";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";

interface LeaseDetailsProps {
  leaseType?: string;
  startDate?: string;
  endDate?: string;
  rent?: string | number;
  securityDeposit?: string | number;
  rentRaw?: string | number;
  securityDepositRaw?: string | number;
  onViewAgreement?: () => void;
  onViewApplication?: () => void;
  tenantData?: any;
}

const LeaseDetails: React.FC<LeaseDetailsProps> = ({
  leaseType,
  startDate,
  endDate,
  rent,
  securityDeposit,
  rentRaw,
  securityDepositRaw,
  onViewAgreement,
  onViewApplication,
  tenantData,
}) => {
  const [loadingType, setLoadingType] = useState<
    "agreement" | "application" | null
  >(null);

  const handleDownload = async (
    docId: number,
    fileName: string,
    type: "agreement" | "application",
  ) => {
    try {
      setLoadingType(type);
      const response = await apiHelper(
        DocumentService.getApiDocumentBase641,
        { showNotification: false },
        docId,
      );

      if (response?.data?.success) {
        convertStreamAndDownload(response.data.success, fileName);
      } else {
        console.error("No PDF found");
      }
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setLoadingType(null);
    }
  };

  const handleViewAgreement = () => {
    if (tenantData?.currentLeaseAgreementDocumentId) {
      handleDownload(
        tenantData.currentLeaseAgreementDocumentId,
        `LeaseAgreement-${tenantData.currentLeaseAgreementDocumentId}.pdf`,
        "agreement",
      );
    } else if (!tenantData?.usedLeaseAgreementTemplate) {
      onViewAgreement?.();
    }
  };

  const handleViewApplication = () => {
    if (tenantData?.currentLeaseApplicationDocumentId) {
      handleDownload(
        tenantData.currentLeaseApplicationDocumentId,
        `LeaseApplication-${tenantData.currentLeaseApplicationDocumentId}.pdf`,
        "application",
      );
    } else if (!tenantData?.usedLeaseApplicationTemplate) {
      onViewApplication?.();
    }
  };

  const isAgreementDisabled =
    !tenantData?.usedLeaseAgreementTemplate &&
    !tenantData?.currentLeaseAgreementDocumentId;
  const isApplicationDisabled =
    !tenantData?.usedLeaseApplicationTemplate &&
    !tenantData?.currentLeaseApplicationDocumentId;

  return (
    <div className="lease-details-card">
      {/* Header */}
      <div className="lease-header">
        <span className="lease-label">Lease Type:</span>
        <span className="lease-badge">{leaseType}</span>
      </div>

      {/* Lease Dates */}
      <div className="lease-dates">
        <div className="date-column">
          <span className="date-label">Start Date</span>
          <span className="date-value">{startDate || "-"}</span>
        </div>
        <div className="date-column">
          <span className="date-label">End Date</span>
          <span className="date-value">{endDate || "-"}</span>
        </div>
      </div>

      {/* Financial Info */}
      <div className="lease-financial">
        <div className="financial-column">
          <span className="financial-label">Security Deposit</span>
          <CurrencyTooltip
            content={securityDepositRaw}
            formatted={securityDeposit}
          >
            <span className="financial-value">{securityDeposit ? `${securityDeposit}` : "-"}</span>
          </CurrencyTooltip>
        </div>
        <div className="financial-column">
          <span className="financial-label">Rent</span>
          <CurrencyTooltip
            content={rentRaw}
            formatted={rent}
          >
            <span className="financial-value">{rent ? `${rent}` : "-"}</span>
          </CurrencyTooltip>
        </div>
      </div>

      {/* Actions */}
      <div className="lease-actions">
        <button
          className="lease-action-button"
          onClick={handleViewAgreement}
          disabled={isAgreementDisabled || loadingType === "agreement"}
          style={{
            opacity: isAgreementDisabled ? 0.5 : 1,
            cursor: isAgreementDisabled ? "not-allowed" : "pointer",
          }}
        >
          {loadingType === "agreement" ? (
            <SimpleSpinner myClassName="Icon20" />
          ) : isAgreementDisabled ? (
            "Agreement Not Available"
          ) : tenantData?.currentLeaseAgreementDocumentId ? (
            "Download Agreement"
          ) : (
            "View Agreement"
          )}
        </button>
        <button
          className="lease-action-button"
          onClick={handleViewApplication}
          disabled={isApplicationDisabled || loadingType === "application"}
          style={{
            opacity: isApplicationDisabled ? 0.5 : 1,
            cursor: isApplicationDisabled ? "not-allowed" : "pointer",
          }}
        >
          {loadingType === "application" ? (
            <SimpleSpinner myClassName="Icon20" />
          ) : isApplicationDisabled ? (
            "Application Not Available"
          ) : tenantData?.currentLeaseApplicationDocumentId ? (
            "Download Application"
          ) : (
            "View Application"
          )}
        </button>
      </div>
    </div>
  );
};

export default LeaseDetails;
